import { NodeType } from '@/features/screenplay/components/editor/nodes/NodeType'
import {
    $createTextNode,
    $getRoot,
    $isElementNode,
    DOMExportOutput,
    EditorConfig,
    ElementNode,
    LexicalEditor,
    LexicalNode,
    NodeKey,
    SerializedElementNode,
    SerializedTextNode,
    Spread,
} from 'lexical'
import { nanoid } from 'nanoid'
import { convertLexicalTextToDBJson, SerializedContent } from '../utils/JsonUtils'
import { isZeroWidthOrEmpty } from '../utils/StringUtils'
import type { SceneNode } from './SceneNode'

//TODO check this output format
//Only optional parameters can be omitted
export type SerializedBaseNode = Spread<
    Omit<SerializedElementNode, 'textStyle' | 'textFormat'>,
    {
        type: string
        version: number
        id: string
    }
>
export abstract class BaseNode extends ElementNode {
    constructor(id: string, key?: NodeKey) {
        super(key)
        this.id = id
    }
    id: string
    abstract next: NodeType | null
    abstract enableSuggestions: boolean
    suggestionPlacement: string | undefined = undefined
    abstract createHtmlElement(_editor?: LexicalEditor): HTMLElement
    isPdfDebug = import.meta.env.VITE_PDF_DEBUG === 'true'

    static create(NodeClass: { new(id: string): BaseNode }, content?: string | null): BaseNode {
        const node = new NodeClass(nanoid())
        node.append($createTextNode(content ?? '\u200B'))
        return node
        //TODO: do we need this $applyNodeReplacement here
        //return $applyNodeReplacement(node)
    }

    updateDOM(prevNode: BaseNode, dom: HTMLElement, config: EditorConfig): boolean {
        return false
    }

    isInline(): boolean {
        return false
    }

    exportJSON(): SerializedBaseNode {
        return {
            ...super.exportJSON(),
            type: this.getType(),
            version: 1,
            id: this.id,
        }
    }
    exportSceneJSON(): SerializedContent {
        return {
            type: this.getType(),
            version: 1,
            id: this.id,
            children: this.getChildren().map((child: LexicalNode, index) =>
                convertLexicalTextToDBJson(child.exportJSON() as SerializedTextNode)
            ),
        }
    }
    createDOM(_config: EditorConfig, _editor: LexicalEditor): HTMLElement {
        return this.debugClassAttachedDom(_editor)
    }
    debugClassAttachedDom(_editor?: LexicalEditor): HTMLElement {
        const dom = this.createHtmlElement(_editor)
        if (!this.isPdfDebug) {
            dom.className += ' node-prod'
        }
        return dom
    }

    exportDOM(): DOMExportOutput {
        return { element: this.debugClassAttachedDom() }
    }

    getPreviousTextContent(): string {
        let textContent = ''
        const children = this.getPreviousSiblings()
        const childrenLength = children.length
        for (let i = 0; i < childrenLength; i++) {
            const child = children[i]
            textContent += child.getTextContent()
            if ($isElementNode(child) && i !== childrenLength - 1 && !child.isInline()) {
                textContent += '\n\n'
            }
        }
        return textContent
    }

    getNextTextContent(): string {
        let textContent = ''
        const children = this.getNextSiblings()
        const childrenLength = children.length
        for (let i = 0; i < childrenLength; i++) {
            const child = children[i]
            textContent += child.getTextContent()
            if ($isElementNode(child) && i !== childrenLength - 1 && !child.isInline()) {
                textContent += '\n\n'
            }
        }
        return textContent
    }

    isEmpty = (): boolean => {
        const text = this.getTextContent()
        return isZeroWidthOrEmpty(text)
        return text === '' || text === '\u200B'
    }

    isNotEmpty = (): boolean => {
        return !this.isEmpty()
    }

    remove(preserveEmptyParent?: boolean): void {
        if ($getRoot().getFirstChild() === this) {
            this.clear()
        } else {
            super.remove()
        }
    }

    getCurrentSceneContent(): SerializedContent[] {
        const res = []

        // Step 1: Find the start node (previous scene or self if already a scene)
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let start: BaseNode = this
        if (this.getType() !== 'scene') {
            let walker = this.getPreviousSibling() as BaseNode
            while (walker) {
                if (walker.getType() === 'scene') {
                    start = walker
                    break
                }
                start = walker
                walker = walker.getPreviousSibling() as BaseNode
            }
        }

        // Step 2: Collect nodes from start until the next scene (not inclusive)
        let current: BaseNode | null = start
        while (current) {
            if (current !== start && current.getType() === 'scene') break
            res.push(current.exportSceneJSON())
            current = current.getNextSibling() as BaseNode
        }

        return res
    }

    getNextSceneNode(): SceneNode | null {
        const nextSiblings = this.getNextSiblings()
        for (const sibling of nextSiblings) {
            if (sibling.getType() === 'scene') {
                return sibling as SceneNode
            }
        }
        return null
    }
    getLastNodeInEditor(): BaseNode | null {
        const children = this.getParent()?.getChildren()
        if (children) return children[children.length - 1] as BaseNode
        return null
    }
}
